import React from 'react';
import { NutritionTrackerProps, MealEntry } from '../../../types/health-tools/engagement-types';
import HealthCard from '../ui/HealthCard';
import ProgressBar from '../ui/ProgressBar';
import { Utensils, Leaf, ArrowRightCircle } from 'lucide-react';

/**
 * Nutrition Tracker Component
 * 
 * Displays nutritional information, goals, and recommendations
 * 
 * @param nutrition - Current nutrition metrics and goals
 * @param mealHistory - Recent meals tracked
 * @param recommendations - Nutritional recommendations
 * @param className - Additional CSS classes
 */
const NutritionTracker: React.FC<NutritionTrackerProps> = (props) => {
  // Default values for the component
  const defaultNutrition = {
    calories: { current: 0, goal: 2000 },
    protein: { current: 0, goal: 120 },
    carbs: { current: 0, goal: 250 },
    fat: { current: 0, goal: 65 },
    water: { current: 0, goal: 8 }
  };
  
  // Helper function to convert from flat format to nested format if needed
  const getNutritionData = () => {
    // Check if props contains the nested nutrition object
    if (props.nutrition) {
      return props.nutrition;
    }
    
    // Check if props contains the flat format (from index.ts interface)
    // @ts-ignore - Handling potential alternative prop structure
    if (props.calorieIntake !== undefined && props.calorieGoal !== undefined) {
      return {
        calories: { 
          // @ts-ignore - Handling potential alternative prop structure
          current: props.calorieIntake, 
          // @ts-ignore - Handling potential alternative prop structure
          goal: props.calorieGoal 
        },
        protein: { 
          // @ts-ignore - Handling potential alternative prop structure
          current: props.protein || 0, 
          goal: 120 
        },
        carbs: { 
          // @ts-ignore - Handling potential alternative prop structure
          current: props.carbs || 0, 
          goal: 250 
        },
        fat: { 
          // @ts-ignore - Handling potential alternative prop structure
          current: props.fat || 0, 
          goal: 65 
        },
        water: { 
          // @ts-ignore - Handling potential alternative prop structure
          current: props.waterIntake || 0, 
          // @ts-ignore - Handling potential alternative prop structure
          goal: props.waterGoal || 8 
        }
      };
    }
    
    // Return default values if neither format is provided
    return defaultNutrition;
  };
  
  // Helper function to get meal history from either prop format
  const getMealHistory = () => {
    // @ts-ignore - Handling potential alternative prop structure
    return props.mealHistory || props.mealLog || [];
  };
  
  // Extract and normalize props for use in the component
  const nutrition = getNutritionData();
  const mealHistory = getMealHistory();
  const recommendations = props.recommendations || [];
  const className = props.className || '';
  
  const getProgressColor = (current: number, goal: number) => {
    const percentage = (current / goal) * 100;
    if (percentage < 50) return 'var(--status-info)';
    if (percentage > 100) return 'var(--status-error)';
    return 'var(--clinical-normal)';
  };

  const formatTime = (timeString: string) => {
    try {
      const date = new Date(timeString);
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch (error) {
      return timeString;
    }
  };

  return (
    <HealthCard 
      title="Nutrition Tracker" 
      icon={Utensils} 
      className={className}
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Nutrition Goals */}
        <div>
          <p className="text-base font-medium mb-3 text-[var(--text-primary)]">Today's Nutrition</p>
          
          <div className="space-y-4">
            <div className="bg-[var(--background-surface)] p-4 rounded-lg">
              <div className="flex justify-between text-sm mb-1">
                <span className="text-[var(--text-secondary)]">Calories</span>
                <span className="text-[var(--text-primary)]">{nutrition.calories.current} / {nutrition.calories.goal} kcal</span>
              </div>
              <ProgressBar 
                value={nutrition.calories.current} 
                max={nutrition.calories.goal} 
                color={getProgressColor(nutrition.calories.current, nutrition.calories.goal)}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-[var(--background-surface)] p-3 rounded-lg">
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-[var(--text-secondary)]">Protein</span>
                  <span className="text-[var(--text-primary)]">{nutrition.protein.current}g</span>
                </div>
                <ProgressBar 
                  value={nutrition.protein.current} 
                  max={nutrition.protein.goal} 
                  color={getProgressColor(nutrition.protein.current, nutrition.protein.goal)}
                />
              </div>
              
              <div className="bg-[var(--background-surface)] p-3 rounded-lg">
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-[var(--text-secondary)]">Carbs</span>
                  <span className="text-[var(--text-primary)]">{nutrition.carbs.current}g</span>
                </div>
                <ProgressBar 
                  value={nutrition.carbs.current} 
                  max={nutrition.carbs.goal} 
                  color={getProgressColor(nutrition.carbs.current, nutrition.carbs.goal)}
                />
              </div>
              
              <div className="bg-[var(--background-surface)] p-3 rounded-lg">
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-[var(--text-secondary)]">Fat</span>
                  <span className="text-[var(--text-primary)]">{nutrition.fat.current}g</span>
                </div>
                <ProgressBar 
                  value={nutrition.fat.current} 
                  max={nutrition.fat.goal} 
                  color={getProgressColor(nutrition.fat.current, nutrition.fat.goal)}
                />
              </div>
              
              <div className="bg-[var(--background-surface)] p-3 rounded-lg">
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-[var(--text-secondary)]">Water</span>
                  <span className="text-[var(--text-primary)]">{nutrition.water.current} / {nutrition.water.goal} cups</span>
                </div>
                <ProgressBar 
                  value={nutrition.water.current} 
                  max={nutrition.water.goal} 
                  color={getProgressColor(nutrition.water.current, nutrition.water.goal)}
                />
              </div>
            </div>
          </div>
          
          <button className="w-full mt-4 py-2 px-4 bg-[var(--brand-primary)] text-white rounded-lg hover:bg-[var(--brand-primary-hover)] flex items-center justify-center">
            <span className="mr-2">Log Meal</span>
            <ArrowRightCircle className="h-4 w-4" />
          </button>
        </div>
        
        {/* Meal History and Recommendations */}
        <div>
          <div className="mb-5">
            <div className="flex justify-between mb-2">
              <p className="text-base font-medium text-[var(--text-primary)]">Recent Meals</p>
              <button className="text-sm text-[var(--brand-primary)]">View All</button>
            </div>
            
            <div className="bg-[var(--background-surface)] rounded-lg p-3">
              {mealHistory.length > 0 ? (
                <div className="space-y-3">
                  {mealHistory.map((meal: MealEntry, index: number) => (
                    <div key={meal.id || index} className="flex justify-between items-center border-b border-[var(--border-subtle)] last:border-0 pb-2 last:pb-0">
                      <div>
                        <p className="text-sm font-medium text-[var(--text-primary)]">{meal.name}</p>
                        <p className="text-xs text-[var(--text-secondary)]">{formatTime(meal.time)}</p>
                      </div>
                      <p className="text-sm text-[var(--text-primary)]">{meal.calories} kcal</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-[var(--text-secondary)] text-sm py-2">
                  No meals logged today.
                </p>
              )}
            </div>
          </div>
          
          <div>
            <div className="flex items-center mb-2">
              <Leaf className="h-4 w-4 text-[var(--clinical-normal)] mr-2" />
              <p className="text-base font-medium text-[var(--text-primary)]">Recommendations</p>
            </div>
            
            <div className="bg-[var(--background-surface)] rounded-lg p-3">
              {recommendations.length > 0 ? (
                <ul className="space-y-2">
                  {recommendations.map((rec: string, index: number) => (
                    <li key={index} className="text-sm text-[var(--text-primary)]">
                      • {rec}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-[var(--text-secondary)] text-sm py-2">
                  Log your meals to get personalized recommendations.
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
      
      <p className="text-xs text-[var(--text-secondary)] mt-4">
        These nutritional goals are based on your personal health profile and activity level.
        Consult with your healthcare provider before making significant dietary changes.
      </p>
    </HealthCard>
  );
};

export default NutritionTracker; 